import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface Toast {
  id: string;
  message: string;
  title?: string;
  subtitle?: string;
  type: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
}

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private toastsSubject = new BehaviorSubject<Toast[]>([]);
  public toasts$ = this.toastsSubject.asObservable();

  private generateId(): string {
    return Math.random().toString(36).substring(2, 15);
  }

  private addToast(toast: Toast): void {
    const currentToasts = this.toastsSubject.value;
    const newToasts = [...currentToasts, toast];
    this.toastsSubject.next(newToasts);

    // Auto-remove toast after duration
    const duration = toast.duration || 5000; // Back to 5 seconds
    setTimeout(() => {
      this.removeToast(toast.id);
    }, duration);
  }

  removeToast(id: string): void {
    const currentToasts = this.toastsSubject.value;
    const filteredToasts = currentToasts.filter(toast => toast.id !== id);
    this.toastsSubject.next(filteredToasts);
  }

  success(message: string, duration?: number): void {
    this.addToast({
      id: this.generateId(),
      message,
      type: 'success',
      duration
    });
  }

  error(message: string, duration?: number): void;
  error(title: string, subtitle: string, duration?: number): void;
  error(messageOrTitle: string, durationOrSubtitle?: number | string, duration?: number): void {
    if (typeof durationOrSubtitle === 'string') {
      // Structured error with title and subtitle
      this.addToast({
        id: this.generateId(),
        message: '', // Keep for backward compatibility
        title: messageOrTitle,
        subtitle: durationOrSubtitle,
        type: 'error',
        duration: duration
      });
    } else {
      // Simple error message
      this.addToast({
        id: this.generateId(),
        message: messageOrTitle,
        type: 'error',
        duration: durationOrSubtitle
      });
    }
  }

  warning(message: string, duration?: number): void {
    this.addToast({
      id: this.generateId(),
      message,
      type: 'warning',
      duration
    });
  }

  info(message: string, duration?: number): void {
    this.addToast({
      id: this.generateId(),
      message,
      type: 'info',
      duration
    });
  }

  clear(): void {
    this.toastsSubject.next([]);
  }
}